/**
 * Wire shapes for edge-script-api (phase 2 plan, Tasks 2.3 and 2.5). Zod is the source
 * of truth; the TypeScript types derive from it. These import nothing from the language
 * package on purpose: the API slice must stay loadable in user builds without dragging
 * the grammar in (see `features/edgescript/flags.ts`).
 */
import { z } from 'zod'

/** Mirrors `Diagnostic` in `packages/edgescript/src/diagnostics.ts`; spans are offsets. */
export const ScriptDiagnosticSchema = z
  .object({
    code: z.string(),
    severity: z.enum(['error', 'warning']).catch('error'),
    message: z.string(),
    span: z.object({ start: z.number(), end: z.number() }),
    suggestion: z.string().optional(),
    fix: z
      .object({ span: z.object({ start: z.number(), end: z.number() }), replacement: z.string() })
      .optional(),
  })
  .passthrough()
export type ScriptDiagnostic = z.infer<typeof ScriptDiagnosticSchema>

export const ScriptInputValueSchema = z.union([z.number(), z.string(), z.boolean()])

/**
 * What the chart shows, for a script's chart.* values (Pine v6 manual). Mirrors
 * `ChartContext` in `packages/edgescript/src/script-context.ts`; the host sends only the
 * values a script reads. Colors are "#rrggbb", times are bar open times in ms.
 */
export const ScriptChartContextSchema = z.object({
  bgColor: z.string().optional(),
  fgColor: z.string().optional(),
  type: z.enum(['standard', 'heikinashi']).optional(),
  leftVisibleBarTime: z.number().optional(),
  rightVisibleBarTime: z.number().optional(),
})
export type ScriptChartContext = z.infer<typeof ScriptChartContextSchema>
export type ScriptInputValue = z.infer<typeof ScriptInputValueSchema>

export const EvaluateScriptRequestSchema = z.object({
  source: z.string().min(1),
  ticker: z.string().min(1),
  /** Server resolution label: `1m`, `5m`, `1h`, `1D`, `1W`, `1M`, or a bare minute count. */
  resolution: z.string().regex(/^\d+[mhDWM]?$/),
  /** Unix seconds, inclusive start of the requested window. */
  fromTs: z.number().int().nonnegative(),
  /** Unix seconds, exclusive end of the requested window. */
  toTs: z.number().int().positive(),
  inputs: z.record(z.string(), ScriptInputValueSchema).optional(),
  /** The chart's extended-hours setting; false evaluates on regular-hours bars. */
  extendedHours: z.boolean().optional(),
  /** chart.* for the scripts that read it: only those values, so other caches stay warm. */
  chart: ScriptChartContextSchema.optional(),
})
export type EvaluateScriptRequest = z.infer<typeof EvaluateScriptRequestSchema>

/**
 * Per-bar colors (Pine v6 manual: the plot kinds' colors are series colors), aligned with
 * `bars`: present only when the color changes; null where it is na, and nothing is drawn
 * on that bar. The fixed field beside it (`color`, ...) is the first bar's color.
 */
const PerBarColors = z.array(z.string().nullable()).optional()

export const EvaluatedPlotSchema = z.object({
  key: z.string(),
  title: z.string(),
  values: z.array(z.number().nullable()),
  color: z.string().optional(),
  /** Per bar (see PerBarColors): a line's segment ending at that bar, a bar, a circle. */
  colors: PerBarColors,
  // the evaluator's styles (packages/edgescript runtime/types.ts); histogram kept for old data
  style: z.enum(['line', 'bar', 'circle', 'histogram', 'columns']).optional(),
  histbase: z.number().optional(),
  /** Pine show_last: values already trimmed to the last n bars; kept to trim live tails too. */
  showLast: z.number().optional(),
  /** Pine display.* bits (pane 1, data window 2, status line 4, price scale 8); absent is all. */
  display: z.number().optional(),
  /** Pine trackprice: a line at the last value across the pane. */
  trackprice: z.boolean().optional(),
})
export type EvaluatedPlot = z.infer<typeof EvaluatedPlotSchema>

/** Pine show_last and display on an annotation kind (see EvaluatedPlotSchema). */
const PineShownSchema = {
  showLast: z.number().optional(),
  display: z.number().optional(),
}
/** A per-bar series: null where the bar has none. */
const PerBarNumbers = z.array(z.number().nullable())

/** `hline(level)`: a horizontal level across the pane. */
export const EvaluatedHlineSchema = z.object({
  key: z.string(),
  title: z.string(),
  level: z.number(),
  color: z.string().optional(),
  /** Pine hline.style_*; absent is dashed, Pine's default. */
  linestyle: z.enum(['solid', 'dotted', 'dashed']).optional(),
  linewidth: z.number().optional(),
  display: z.number().optional(),
})
/** `fill(a, b)`: a band between two series, one value per bar each. */
export const EvaluatedFillSchema = z.object({
  key: z.string(),
  title: z.string().optional(),
  color: z.string().optional(),
  /** Per bar (PerBarColors): the band's segment ending at that bar. */
  colors: PerBarColors,
  a: z.array(z.number().nullable()),
  b: z.array(z.number().nullable()),
  /** Pine fill's gradient form, per bar: top_color at `top`, bottom_color at `bottom`. */
  top: PerBarNumbers.optional(),
  bottom: PerBarNumbers.optional(),
  topColors: z.array(z.string().nullable()).optional(),
  bottomColors: z.array(z.string().nullable()).optional(),
  ...PineShownSchema,
})
/** `plotshape(when)`: a marker on each bar where `when` is true. */
export const EvaluatedShapeSchema = z.object({
  key: z.string(),
  title: z.string(),
  icon: z.string(),
  /** Pine location: above or below the bar, the pane's top or bottom, or at `prices`. */
  at: z.enum(['above', 'below', 'top', 'bottom', 'absolute']),
  color: z.string().optional(),
  values: z.array(z.boolean()),
  /** location.absolute: the price per bar. */
  prices: PerBarNumbers.optional(),
  /** Pine size.*; absent is auto. */
  size: z.string().optional(),
  text: z.string().optional(),
  textColor: z.string().optional(),
  colors: PerBarColors,
  textColors: PerBarColors,
  ...PineShownSchema,
})
/** `plotchar(series)`: a character on each bar where the series is true or non-zero. */
export const EvaluatedCharSchema = z.object({
  key: z.string(),
  title: z.string(),
  char: z.string(),
  at: z.enum(['abovebar', 'belowbar', 'top', 'bottom', 'absolute']),
  color: z.string().optional(),
  size: z.string(),
  /** The series value where drawn (true is 1), null where not. */
  values: z.array(z.number().nullable()),
  text: z.string().optional(),
  textColor: z.string().optional(),
  colors: PerBarColors,
  textColors: PerBarColors,
  ...PineShownSchema,
})
/** `plotarrow(series)`: up for positive, down for negative, sized by |value|. */
export const EvaluatedArrowSchema = z.object({
  key: z.string(),
  title: z.string(),
  colorUp: z.string().optional(),
  colorDown: z.string().optional(),
  colorsUp: PerBarColors,
  colorsDown: PerBarColors,
  minHeight: z.number(),
  maxHeight: z.number(),
  values: z.array(z.number().nullable()),
  ...PineShownSchema,
})
/** `plotcandle` / `plotbar`: OHLC from four series, null where the bar is skipped. */
export const EvaluatedCandleSeriesSchema = z.object({
  key: z.string(),
  title: z.string(),
  kind: z.enum(['candle', 'bar']),
  open: z.array(z.number().nullable()),
  high: z.array(z.number().nullable()),
  low: z.array(z.number().nullable()),
  close: z.array(z.number().nullable()),
  /** Per bar; null keeps the up/down default. */
  colors: z.array(z.string().nullable()),
  wickColor: z.string().optional(),
  borderColor: z.string().optional(),
  /** Per bar (PerBarColors); null keeps the fixed fallback. */
  wickColors: PerBarColors,
  borderColors: PerBarColors,
  ...PineShownSchema,
})
/** A drawing's price: na travels as null (JSON has no NaN) and reads back as NaN. */
const DrawingPriceSchema = z
  .number()
  .nullable()
  .transform((v) => v ?? Number.NaN)

/**
 * `line.new` / `label.new` / `box.new` / `polyline.new` / `linefill.new` as they stand after
 * the last bar; t = timestamp (ms), the placement the chart reads (a drawing placed by time
 * keeps its times). A polyline or linefill carries its outline in `points`. Options (Pine
 * v6 manual, label.new, line.new, box.new) are absent at their defaults: yloc price, size
 * normal (a box's auto), textAlign and textValign center, no extend, no wrap, the default
 * font, no formatting, a box border 1px solid. A line's width and style are always sent.
 */
export const EvaluatedDrawingSchema = z.object({
  id: z.number(),
  kind: z.enum(['line', 'label', 'box', 'polyline', 'linefill']),
  x1: z.number(),
  t1: z.number(),
  // na when a label sits above or below the bar (yloc), where y is unused
  y1: DrawingPriceSchema,
  x2: z.number().optional(),
  t2: z.number().optional(),
  y2: DrawingPriceSchema.optional(),
  text: z.string().optional(),
  color: z.string().optional(),
  /** A box's fill; a polyline's fill_color. */
  bgColor: z.string().optional(),
  /** Line or polyline width; a box's border width. */
  width: z.number().optional(),
  /** line.style_* for a line or polyline, a box's border style, a label's label.style_*. */
  style: z.string().optional(),
  /** extend.*: a line, box, or linefill runs on to the pane edge. */
  extend: z.enum(['left', 'right', 'both']).optional(),
  points: z.array(z.object({ x: z.number(), t: z.number(), y: z.number() })).optional(),
  closed: z.boolean().optional(),
  curved: z.boolean().optional(),
  line1: z.number().optional(),
  line2: z.number().optional(),
  yloc: z.enum(['price', 'abovebar', 'belowbar']).optional(),
  textColor: z.string().optional(),
  size: z
    .union([z.enum(['auto', 'tiny', 'small', 'normal', 'large', 'huge']), z.number()])
    .optional(),
  textAlign: z.enum(['left', 'center', 'right']).optional(),
  /** A box's text_valign. */
  textValign: z.enum(['top', 'bottom']).optional(),
  /** A box's text wrapped to its width (text.wrap_auto). */
  textWrap: z.boolean().optional(),
  /** Recorded by the evaluator; the chart does not show it yet. */
  tooltip: z.string().optional(),
  fontFamily: z.literal('monospace').optional(),
  bold: z.boolean().optional(),
  italic: z.boolean().optional(),
})
export type EvaluatedHline = z.infer<typeof EvaluatedHlineSchema>
export type EvaluatedFill = z.infer<typeof EvaluatedFillSchema>
export type EvaluatedShape = z.infer<typeof EvaluatedShapeSchema>
export type EvaluatedChar = z.infer<typeof EvaluatedCharSchema>
export type EvaluatedArrow = z.infer<typeof EvaluatedArrowSchema>
export type EvaluatedCandleSeries = z.infer<typeof EvaluatedCandleSeriesSchema>
export type EvaluatedDrawing = z.infer<typeof EvaluatedDrawingSchema>
/** One defined cell of a script table; width and height are percents of the pane, 0 = auto. */
export const EvaluatedTableCellSchema = z.object({
  column: z.number(),
  row: z.number(),
  text: z.string(),
  width: z.number().default(0),
  height: z.number().default(0),
  textHalign: z.enum(['left', 'center', 'right']).default('center'),
  textValign: z.enum(['top', 'center', 'bottom']).default('center'),
  textSize: z
    .union([z.enum(['auto', 'tiny', 'small', 'normal', 'large', 'huge']), z.number()])
    .default('normal'),
  textColor: z.string().optional(),
  bgColor: z.string().optional(),
  tooltip: z.string().optional(),
})
/** `table.new` as it stands after the last bar, pinned to one of nine anchors on the pane. */
export const EvaluatedTableSchema = z.object({
  id: z.number(),
  position: z.enum([
    'top_left',
    'top_center',
    'top_right',
    'middle_left',
    'middle_center',
    'middle_right',
    'bottom_left',
    'bottom_center',
    'bottom_right',
  ]),
  columns: z.number(),
  rows: z.number(),
  cells: z.array(EvaluatedTableCellSchema).default([]),
  merges: z
    .array(
      z.object({
        startColumn: z.number(),
        startRow: z.number(),
        endColumn: z.number(),
        endRow: z.number(),
      }),
    )
    .default([]),
  frameWidth: z.number().default(0),
  borderWidth: z.number().default(0),
  bgColor: z.string().optional(),
  frameColor: z.string().optional(),
  borderColor: z.string().optional(),
})
export type EvaluatedTableCell = z.infer<typeof EvaluatedTableCellSchema>
export type EvaluatedTable = z.infer<typeof EvaluatedTableSchema>

/**
 * `alertcondition` (one per declaration, the bars where it was true) and fired `alert()`
 * calls; bar indexes align with `bars`. Recorded only: nothing is sent.
 */
export const EvaluatedAlertSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('condition'),
    title: z.string(),
    message: z.string(),
    bars: z.array(z.number()),
  }),
  z.object({ kind: z.literal('alert'), message: z.string(), bar: z.number() }),
])
export type EvaluatedAlert = z.infer<typeof EvaluatedAlertSchema>

/** One log.info / log.warning / log.error call that ran on a closed bar. */
export const EvaluatedLogSchema = z.object({
  level: z.enum(['info', 'warning', 'error']).catch('info'),
  message: z.string(),
  bar: z.number(),
  time: z.number(),
})
export type EvaluatedLog = z.infer<typeof EvaluatedLogSchema>

export const EvaluateScriptResponseSchema = z
  .object({
    scriptHash: z.string(),
    /** Bar timestamps in milliseconds, ascending, aligned with every plot's values. */
    bars: z.array(z.number()),
    plots: z.array(EvaluatedPlotSchema),
    inputs: z
      .array(
        z
          .object({
            key: z.string(),
            // an untitled input (input.int(14), Pine's title = "") is keyed input_<n>, no title
            title: z.string().optional(),
            type: z.string(),
            default: z.unknown().optional(),
          })
          .passthrough(),
      )
      .optional(),
    warmupBars: z.number(),
    diagnostics: z.array(ScriptDiagnosticSchema),
    cached: z.boolean().optional(),
    hlines: z.array(EvaluatedHlineSchema).default([]),
    fills: z.array(EvaluatedFillSchema).default([]),
    shapes: z.array(EvaluatedShapeSchema).default([]),
    chars: z.array(EvaluatedCharSchema).default([]),
    arrows: z.array(EvaluatedArrowSchema).default([]),
    candles: z.array(EvaluatedCandleSeriesSchema).default([]),
    /** Per bar, aligned with `bars`; null keeps the bar's own color; empty when unused. */
    barcolors: z.array(z.string().nullable()).default([]),
    /** Per bar, aligned with `bars`; null where unshaded; empty when the script never shades. */
    bgcolors: z.array(z.string().nullable()).default([]),
    drawings: z.array(EvaluatedDrawingSchema).default([]),
    /**
     * Drawings made and removed per kind over the run (warm-up included); absent when the
     * script drew nothing. `drawings` is only what is left at the end.
     */
    drawingCounts: z
      .record(z.string(), z.object({ created: z.number(), removed: z.number() }))
      .optional(),
    tables: z.array(EvaluatedTableSchema).default([]),
    /** Absent when the script declares no alertcondition and fires no alert(). */
    alerts: z.array(EvaluatedAlertSchema).optional(),
    /** log.* entries, newest 500 of the run; bar aligns with `bars` (negative = warm-up). */
    logs: z.array(EvaluatedLogSchema).optional(),
    /** Older entries dropped to keep `logs` within its cap. */
    logsDropped: z.number().optional(),
  })
  .passthrough()
export type EvaluateScriptResponse = z.infer<typeof EvaluateScriptResponseSchema>

/** 422 body: the script failed to compile. */
export const EvaluateScriptFailureSchema = z
  .object({ diagnostics: z.array(ScriptDiagnosticSchema) })
  .passthrough()
export type EvaluateScriptFailure = z.infer<typeof EvaluateScriptFailureSchema>

export const CompileScriptResponseSchema = z
  .object({
    ok: z.boolean(),
    profile: z.string().optional(),
    diagnostics: z.array(ScriptDiagnosticSchema),
    formatted: z.string().optional(),
    lookbackBars: z.number().optional(),
  })
  .passthrough()
export type CompileScriptResponse = z.infer<typeof CompileScriptResponseSchema>

/**
 * A script indicator pinned to a chart panel (`ChartPanelState.scriptIndicators`).
 * Carries the source inline so a layout reloads without a My scripts fetch and keeps
 * drawing the exact text the user added; `scriptId` and `scriptVersion` link it back to
 * the saved entity once Task 2.4 lands. `calcParams` follows the catalog convention
 * (drawer order), `styles` is keyed by plot key.
 */
export const ScriptIndicatorEntrySchema = z
  .object({
    /** Stable per-panel id; also the klinecharts instance name suffix. */
    id: z.string().min(1),
    source: z.string().min(1),
    scriptId: z.string().optional(),
    scriptVersion: z.number().int().optional(),
    calcParams: z.array(ScriptInputValueSchema).optional(),
    styles: z.record(z.string(), z.unknown()).optional(),
    visible: z.boolean().optional(),
    paneHeightRatio: z.number().optional(),
  })
  .passthrough()
export type ScriptIndicatorEntry = z.infer<typeof ScriptIndicatorEntrySchema>

// ─── My scripts (edge-script-api /v1/edgescript/scripts, Task 2.4) ───

export const ScriptProfileSchema = z.enum(['indicator', 'strategy', 'library'])
export type ScriptProfile = z.infer<typeof ScriptProfileSchema>

/** The list projection: never carries `source`. */
export const UserScriptSummarySchema = z.object({
  scriptId: z.string(),
  name: z.string(),
  profile: ScriptProfileSchema,
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type UserScriptSummary = z.infer<typeof UserScriptSummarySchema>

export const UserScriptSchema = UserScriptSummarySchema.extend({ source: z.string() })
export type UserScript = z.infer<typeof UserScriptSchema>

export const ListScriptsResponseSchema = z.object({ scripts: z.array(UserScriptSummarySchema) })

export const PutScriptRequestSchema = z.object({
  scriptId: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/),
  name: z.string().min(1).max(256),
  profile: ScriptProfileSchema,
  source: z.string().min(1).max(50_000),
})
export type PutScriptRequest = z.infer<typeof PutScriptRequestSchema>

export const PutScriptResponseSchema = z.object({
  scriptId: z.string(),
  version: z.number().int(),
  createdAt: z.string(),
  updatedAt: z.string(),
})
export type PutScriptResponse = z.infer<typeof PutScriptResponseSchema>

export const ScriptVersionSchema = z.object({
  version: z.number().int(),
  savedAt: z.string(),
  source: z.string(),
})
export type ScriptVersion = z.infer<typeof ScriptVersionSchema>

export const ListScriptVersionsResponseSchema = z.object({
  versions: z.array(ScriptVersionSchema),
})
